import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YaloThemeProvider } from '@engyalo/design-system';
import { datadogRum } from '@datadog/browser-rum';
import { LicenseInfo } from '@mui/x-data-grid-pro';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Provider } from 'react-redux';
import { manifestBase } from 'constants/manifest';
import RoutesApp from './routes';
import './App.css';
import './i18n';
import './index.css';
import pkgjson from '../package.json';
import { loadSegment } from './helpers/segment';
import { setBaseUrlAxiosInstance } from './services/AxiosInstance';
import { setBaseUrlAxiosInstanceYalo } from './services/AxiosInstanceYalo';
import useEnvironment from './hooks/useEnvironments';
import store from './redux/store';
import ErrorModal from './components/ErrorModal';

loadSegment();

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_PRO_KEY || 'unknown');

export enum ignoreErrors {
  NETWORK_ERROR = 'Network Error',
  ERROR_401 = 'Request failed with status code 401',
  IGNORED_EVENT_CANCEL = 'IgnoredEventCancel',
}

export const rumBeforeSend = (event: any) => {
  if (event.type === 'error') {
    const isFalsePositiveError = Object.values(ignoreErrors).find(
      (ignoreErrorString) =>
        event.error.message?.includes(ignoreErrorString) || event.error.stack?.includes(ignoreErrorString)
    );
    if (isFalsePositiveError) {
      return false;
    }
  }
  return true;
};

if (process.env.REACT_APP_DD_TOKEN) {
  datadogRum.init({
    applicationId: `${process.env.REACT_APP_DD_ID}`,
    clientToken: `${process.env.REACT_APP_DD_TOKEN}`,
    site: `${process.env.REACT_APP_DD_SITE}`,
    service: `${process.env.REACT_APP_DD_SERVICE}`,
    env: `${process.env.REACT_APP_DD_ENV}`,
    version: `${pkgjson.version}`,
    sampleRate: 100,
    premiumSampleRate: 100,
    trackInteractions: true,
    trackFrustrations: true,
    beforeSend: rumBeforeSend,
  });
  datadogRum.startSessionReplayRecording();
}

const noop = () => {};

const App = () => {
  const { getInstance, getUrl } = useEnvironment();
  const { t } = useTranslation();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const generateManifest = (instance: string) => {
    const manifest = {
      ...manifestBase,
      start_url: `/${instance}/`,
      scope: `/${instance}/`,
    };

    const link = document.createElement('link');
    link.rel = 'manifest';
    const stringManifest = JSON.stringify(manifest);
    link.setAttribute('href', `data:application/json;charset=utf-8,${encodeURIComponent(stringManifest)}`);
    document.head.appendChild(link);
  };

  useEffect(() => {
    const instance = getInstance();
    const url = getUrl('http');

    async function checkInstance() {
      try {
        await fetch(url, { method: 'HEAD' });
        if (instance) {
          generateManifest(instance);
        }
      } catch {
        setShowErrorModal(true);
      }
    }

    setBaseUrlAxiosInstance(url);
    setBaseUrlAxiosInstanceYalo(instance);
    checkInstance();
  }, []);

  return (
    <Provider store={store}>
      <YaloThemeProvider>
        {showErrorModal ? (
          <ErrorModal
            open={showErrorModal}
            title={t('invalidInstanceTitle')}
            message={t('invalidInstanceMessage')}
            handleClose={noop}
          />
        ) : (
          <RoutesApp />
        )}
      </YaloThemeProvider>
    </Provider>
  );
};

export default App;
